import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCreateOwner, type Owner } from "@/hooks/useOwners";
import { useCreatePet, useNextPetRecordNumber } from "@/hooks/usePets";
import {
  useDocumentTypes,
  useGenders,
  useLeadSources,
  usePetStatuses,
  useStates,
  type City,
} from "@/hooks/useLookups";
import { type OwnerInput } from "@/lib/validations/owner";
import { ownerWizardSchema } from "@/lib/validations/ownerWizard";
import { type PetInput } from "@/lib/validations/pet";
import { petWizardSchema } from "@/lib/validations/petWizard";
import { OwnerFormSection } from "@/components/owners/OwnerFormSection";
import { PetFormSection } from "@/components/owners/PetFormSection";
import { Form } from "@/components/ui/form";
import { Button } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { maskDateInput } from "@/lib/date-utils";
import { applyDocumentMask } from "@/lib/utils/document-masks";
import { CepLookupError, lookupCep, mapCepErrorToMessage } from "@/lib/cep";
import { startCancelableSmoothScroll } from "@/lib/cancelableSmoothScroll";

type RegistrationMode = "owner_and_pet" | "pet_only";

type QuickRegisterPatientDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: RegistrationMode;
  initialOwnerId?: string;
  initialOwnerName?: string;
  onCompleted: (payload: { ownerId: string; ownerName: string; petId: string; petName: string }) => void;
};

const ownerDefaultValues: OwnerInput = {
  name: "",
  gender_id: "",
  document_type_id: "",
  document: "",
  birth_date: "",
  profession: "",
  notes: "",
  marketing_consent: true,
  lead_source_id: "",
  phones: [{ phone_number: "", is_whatsapp: true, is_primary: true }],
  emails: [{ email: "", is_primary: true }],
  addresses: [
    {
      zip_code: "",
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city_id: "",
      is_primary: true,
    },
  ],
};

const petDefaultValues: PetInput = {
  name: "",
  owner_id: "",
  record_number: "",
  gender_id: "",
  sterilization_id: "",
  birth_date: "",
  species_id: "",
  breed_id: "",
  coat_type_id: "",
  color_id: "",
  status_id: "",
  microchip_number: "",
  health_plan: "",
  pedigree_number: "",
  notes: "",
  allergies: [],
  temperaments: [],
  photo: null,
  attachments: [],
};

export function QuickRegisterPatientDialog({
  open,
  onOpenChange,
  mode,
  initialOwnerId,
  initialOwnerName,
  onCompleted,
}: QuickRegisterPatientDialogProps) {
  const cancelScrollRef = useRef<(() => void) | null>(null);
  const { t } = useTranslation(["calendar", "common"]);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const createOwner = useCreateOwner();
  const createPet = useCreatePet();
  const {
    data: nextRecordNumber,
    isLoading: isLoadingNextRecordNumber,
    isError: isNextRecordNumberError,
  } = useNextPetRecordNumber();
  const { data: genders } = useGenders();
  const { data: documentTypes } = useDocumentTypes();
  const { data: leadSources } = useLeadSources();
  const { data: states } = useStates();
  const { data: petStatuses } = usePetStatuses();

  const [step, setStep] = useState<"owner" | "pet">(mode === "pet_only" ? "pet" : "owner");
  const [createdOwner, setCreatedOwner] = useState<Pick<Owner, "id" | "name"> | null>(null);
  const [birthDateInput, setBirthDateInput] = useState("");
  const [isBirthDateCalendarOpen, setIsBirthDateCalendarOpen] = useState(false);
  const [documentInput, setDocumentInput] = useState("");
  const [addressStates, setAddressStates] = useState<(string | null)[]>([]);
  const [addressCities, setAddressCities] = useState<Record<number, City[]>>({});
  const [addressCepLoading, setAddressCepLoading] = useState<Record<number, boolean>>({});
  const [cityNameCache, setCityNameCache] = useState<Record<string, string>>({});
  const [cityDisplayValues, setCityDisplayValues] = useState<Record<number, string>>({});
  const dialogContentRef = useRef<HTMLDivElement | null>(null);

  const ownerForm = useForm<OwnerInput>({
    resolver: zodResolver(ownerWizardSchema),
    defaultValues: ownerDefaultValues,
  });

  const petForm = useForm<PetInput>({
    resolver: zodResolver(petWizardSchema),
    defaultValues: petDefaultValues,
  });
  const watchedOwnerPhones = ownerForm.watch("phones");
  const watchedOwnerAddresses = ownerForm.watch("addresses");

  const ownerIdForPet = useMemo(
    () => createdOwner?.id || initialOwnerId || "",
    [createdOwner?.id, initialOwnerId],
  );
  const isNextRecordNumberInvalid = useMemo(() => {
    if (nextRecordNumber === null || nextRecordNumber === undefined) return false;
    return !Number.isFinite(Number(nextRecordNumber));
  }, [nextRecordNumber]);

  useEffect(() => {
    if (!open) return;
    setStep(mode === "pet_only" ? "pet" : "owner");
    setCreatedOwner(null);
    ownerForm.reset({
      ...ownerDefaultValues,
      name: mode === "owner_and_pet" ? (initialOwnerName?.trim() ?? "") : "",
    });
    petForm.reset({
      ...petDefaultValues,
      owner_id: mode === "pet_only" ? (initialOwnerId ?? "") : "",
    });
    setBirthDateInput("");
    setDocumentInput("");
    setAddressStates([]);
    setAddressCities({});
    setAddressCepLoading({});
    setCityNameCache({});
    setCityDisplayValues({});
  }, [initialOwnerId, initialOwnerName, mode, open, ownerForm, petForm]);

  useEffect(() => {
    const phones = watchedOwnerPhones;
    if (!phones) return;
    const normalized = phones.map((phone, index) => ({ ...phone, is_primary: index === 0 }));
    if (JSON.stringify(normalized) !== JSON.stringify(phones)) {
      ownerForm.setValue("phones", normalized, { shouldDirty: false, shouldValidate: false });
    }
  }, [ownerForm, watchedOwnerPhones]);

  useEffect(() => {
    const addresses = watchedOwnerAddresses;
    if (!addresses) return;
    const normalized = addresses.map((address, index) => ({ ...address, is_primary: index === 0 }));
    if (JSON.stringify(normalized) !== JSON.stringify(addresses)) {
      ownerForm.setValue("addresses", normalized, { shouldDirty: false, shouldValidate: false });
    }
  }, [ownerForm, watchedOwnerAddresses]);

  useEffect(() => {
    const addresses = watchedOwnerAddresses ?? [];
    setAddressStates((prev) => addresses.map((_, index) => prev[index] ?? null));
  }, [watchedOwnerAddresses]);

  useEffect(() => {
    if (!open) return;
    const currentStatusId = petForm.getValues("status_id");
    if (currentStatusId) return;
    const defaultStatus = petStatuses?.find((status) => status.name === "Ativo") ?? petStatuses?.[0];
    if (defaultStatus) {
      petForm.setValue("status_id", defaultStatus.id, { shouldDirty: false });
    }
  }, [open, petForm, petStatuses]);

  useEffect(() => {
    if (!open || !nextRecordNumber) return;
    const currentValue = petForm.getValues("record_number");
    if (!currentValue) {
      petForm.setValue("record_number", String(nextRecordNumber), { shouldDirty: true });
    }
  }, [nextRecordNumber, open, petForm]);

  useEffect(() => {
    if (!ownerIdForPet) return;
    petForm.setValue("owner_id", ownerIdForPet, { shouldDirty: false });
  }, [ownerIdForPet, petForm]);

  useEffect(() => {
    if (!open || step !== "pet") return;
    if (!dialogContentRef.current) return;

    cancelScrollRef.current?.();
    cancelScrollRef.current = startCancelableSmoothScroll(dialogContentRef.current, {
      top: 0,
    });
  }, [open, step]);

  useEffect(() => {
    return () => {
      cancelScrollRef.current?.();
    };
  }, []);

  const fetchCitiesForState = useCallback(
    async (stateId: string, index: number, cityNameToSelect?: string) => {
      if (!stateId) {
        setAddressCities((prev) => {
          const updated = { ...prev };
          delete updated[index];
          return updated;
        });
        ownerForm.setValue(`addresses.${index}.city_id`, "", { shouldDirty: true });
        return;
      }

      const { data, error } = await supabase
        .from("cities")
        .select("id, name, state_id")
        .eq("state_id", stateId)
        .order("name");

      if (error || !data) return;

      const typedData = data as City[];
      setAddressCities((prev) => ({ ...prev, [index]: typedData }));
      setCityNameCache((prev) => {
        const updated = { ...prev };
        typedData.forEach((city) => {
          updated[city.id] = city.name;
        });
        return updated;
      });

      if (cityNameToSelect) {
        const match = typedData.find((city) => city.name.toLowerCase() === cityNameToSelect.toLowerCase());
        if (match) {
          ownerForm.setValue(`addresses.${index}.city_id`, match.id, { shouldDirty: true });
        }
      }
    },
    [ownerForm],
  );

  const handleAddAddress = useCallback(() => {
    const current = ownerForm.getValues("addresses") ?? [];
    ownerForm.setValue(
      "addresses",
      [
        ...current,
        {
          zip_code: "",
          street: "",
          number: "",
          complement: "",
          neighborhood: "",
          city_id: "",
          is_primary: false,
        },
      ],
      { shouldDirty: true },
    );
  }, [ownerForm]);

  const handleRemoveAddress = useCallback(
    (index: number) => {
      const current = ownerForm.getValues("addresses") ?? [];
      if (current.length <= 1) return;
      const next = current.filter((_, i) => i !== index);
      ownerForm.setValue(
        "addresses",
        next.map((address, i) => ({ ...address, is_primary: i === 0 })),
        { shouldDirty: true },
      );
      setAddressStates((prev) => prev.filter((_, i) => i !== index));
      setAddressCities((prev) => {
        const updated: Record<number, City[]> = {};
        Object.entries(prev).forEach(([key, value]) => {
          const originalIndex = Number(key);
          if (originalIndex < index) updated[originalIndex] = value;
          if (originalIndex > index) updated[originalIndex - 1] = value;
        });
        return updated;
      });
    },
    [ownerForm],
  );

  const handleCepLookup = useCallback(
    async (index: number) => {
      const cepValue = (ownerForm.getValues(`addresses.${index}.zip_code`) as string) ?? "";
      const cepDigits = cepValue.replace(/\D/g, "");
      if (cepDigits.length !== 8) {
        ownerForm.setError(`addresses.${index}.zip_code`, {
          type: "manual",
          message: "Informe um CEP válido (8 dígitos)",
        });
        return;
      }

      try {
        setAddressCepLoading((prev) => ({ ...prev, [index]: true }));
        const data = await lookupCep(cepDigits);

        ownerForm.setValue(`addresses.${index}.street`, data.street || "", { shouldDirty: true });
        ownerForm.setValue(`addresses.${index}.neighborhood`, data.neighborhood || "", { shouldDirty: true });
        ownerForm.setValue(`addresses.${index}.complement`, data.complement || "", { shouldDirty: true });

        const stateMatch = states?.find((state) => state.code === data.state);
        if (stateMatch) {
          setAddressStates((prev) => {
            const updated = [...prev];
            updated[index] = stateMatch.id;
            return updated;
          });
          await fetchCitiesForState(stateMatch.id, index, data.city);
        }
      } catch (error) {
        const code = error instanceof CepLookupError ? error.code : "LOOKUP_FAILED";
        ownerForm.setError(`addresses.${index}.zip_code`, {
          type: "manual",
          message: mapCepErrorToMessage(code),
        });
      } finally {
        setAddressCepLoading((prev) => ({ ...prev, [index]: false }));
      }
    },
    [fetchCitiesForState, ownerForm, states],
  );

  const handleSaveOwner = ownerForm.handleSubmit(async (values) => {
    const created = await createOwner.mutateAsync(values);
    if (!created?.id) return;
    setCreatedOwner({ id: created.id, name: created.name });
    petForm.setValue("owner_id", created.id, { shouldDirty: false });
    setStep("pet");
    toast({
      title: t("calendar:quickRegister.ownerCreatedTitle"),
      description: t("calendar:quickRegister.ownerCreatedDescription"),
    });
  });

  const handleSavePet = petForm.handleSubmit(async (values) => {
    const ownerId = values.owner_id || ownerIdForPet;
    if (!ownerId) {
      toast({
        title: t("calendar:quickRegister.ownerRequiredTitle"),
        description: t("calendar:quickRegister.ownerRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    const createdPet = await createPet.mutateAsync({
      ...values,
      owner_id: ownerId,
    });

    if (!createdPet?.id) return;

    await queryClient.invalidateQueries({ queryKey: ["quick-start-attendance-pets"] });

    onCompleted({
      ownerId,
      ownerName: createdOwner?.name || initialOwnerName || "",
      petId: createdPet.id,
      petName: createdPet.name,
    });
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className="max-h-[90vh] max-w-5xl">
        <DialogHeader>
          <DialogTitle>
            {step === "owner" ? t("calendar:quickRegister.registerOwnerTitle") : t("calendar:quickRegister.registerPatientTitle")}
          </DialogTitle>
          <DialogDescription>
            {mode === "pet_only"
              ? t("calendar:quickRegister.petOnlyDescription")
              : t("calendar:quickRegister.ownerAndPetDescription")}
          </DialogDescription>
        </DialogHeader>

        {step === "owner" ? (
          <Form {...ownerForm}>
            <form onSubmit={(event) => event.preventDefault()} className="space-y-4">
              <OwnerFormSection
                form={ownerForm}
                genders={genders}
                documentTypes={documentTypes}
                leadSources={leadSources}
                states={states}
                birthDateInput={birthDateInput}
                setBirthDateInput={(value) => {
                  const maskedValue = maskDateInput(value);
                  setBirthDateInput(maskedValue);
                }}
                isBirthDateCalendarOpen={isBirthDateCalendarOpen}
                setIsBirthDateCalendarOpen={setIsBirthDateCalendarOpen}
                documentInput={documentInput}
                setDocumentInput={(value) => {
                  const selectedType = documentTypes?.find((type) => type.id === ownerForm.getValues("document_type_id"));
                  const maskedValue = applyDocumentMask(value, selectedType);
                  setDocumentInput(maskedValue);
                  ownerForm.setValue("document", maskedValue, { shouldDirty: true });
                }}
                phones={watchedOwnerPhones ?? []}
                addresses={watchedOwnerAddresses ?? []}
                addressStates={addressStates}
                setAddressStates={(updater) => setAddressStates(updater)}
                addressCities={addressCities}
                addressCepLoading={addressCepLoading}
                cityNameCache={cityNameCache}
                setCityNameCache={(updater) => setCityNameCache(updater)}
                cityDisplayValues={cityDisplayValues}
                setCityDisplayValues={(updater) => setCityDisplayValues(updater)}
                fetchCitiesForState={fetchCitiesForState}
                handleAddAddress={handleAddAddress}
                handleRemoveAddress={handleRemoveAddress}
                handleCepLookup={handleCepLookup}
              />
            </form>
          </Form>
        ) : (
          <Form {...petForm}>
            <form onSubmit={(event) => event.preventDefault()} className="space-y-4">
              <PetFormSection
                form={petForm}
                index={0}
                showRemoveButton={false}
                recordNumberUi={
                  isLoadingNextRecordNumber && !nextRecordNumber
                    ? { disabled: true, placeholder: t("calendar:quickRegister.recordLoadingPlaceholder") }
                    : (isNextRecordNumberError || isNextRecordNumberInvalid)
                      ? { disabled: false, placeholder: t("calendar:quickRegister.recordManualPlaceholder") }
                      : { disabled: false, placeholder: t("calendar:quickRegister.recordExamplePlaceholder") }
                }
              />
            </form>
          </Form>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common:cancel")}
          </Button>
          {step === "owner" ? (
            <Button
              onClick={handleSaveOwner}
              disabled={createOwner.isPending}
            >
              {createOwner.isPending ? t("calendar:quickRegister.saving") : t("calendar:quickRegister.saveOwner")}
            </Button>
          ) : (
            <Button
              onClick={handleSavePet}
              disabled={createPet.isPending || !ownerIdForPet}
            >
              {createPet.isPending ? t("calendar:quickRegister.saving") : t("calendar:quickRegister.savePatient")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
